/**
 * The six phases of the v2 returns pipeline. Mirrors the server-side
 * `ReturnPhaseEnum` constants. Stored as varchar(20) on
 * `ReturnRequestDetail.phase`.
 *
 * Only `Closed` is terminal; `Unreconciled` is a non-terminal holding
 * state requiring operator reconciliation. The state-machine transition
 * graph lives on the server (`ReturnPhaseStateMachine`); the client only
 * needs the constants to drive UI visibility and labels.
 */
export enum ReturnPhase {
  Receiving    = 'RECEIVING',
  Inspecting   = 'INSPECTING',
  Routing      = 'ROUTING',
  Settling     = 'SETTLING',
  Unreconciled = 'UNRECONCILED',
  Closed       = 'CLOSED',
}

/** Phase labels for UI display — diverges from raw codes (title-case, no shout-case). */
export const ReturnPhaseLabel: Record<ReturnPhase, string> = {
  [ReturnPhase.Receiving]:    'Receiving',
  [ReturnPhase.Inspecting]:   'Inspecting',
  [ReturnPhase.Routing]:      'Routing',
  [ReturnPhase.Settling]:     'Settling',
  [ReturnPhase.Unreconciled]: 'Unreconciled',
  [ReturnPhase.Closed]:       'Closed',
};

/** True when the RRD has reached the only terminal phase. */
export function isClosed(phase: string | null | undefined): boolean {
  return phase === ReturnPhase.Closed;
}

/** True when the RRD is in active processing (anything other than Closed). */
export function isActive(phase: string | null | undefined): boolean {
  return !!phase && phase !== ReturnPhase.Closed;
}

/**
 * True when the RRD belongs to the v1 flow (NULL phase). v2 UI must refuse
 * to operate on these — they're driven by the legacy ReturnRequestDetailStatusId.
 */
export function isV1(phase: string | null | undefined): boolean {
  return phase == null;
}
